import { useBatchLicenseValidation } from './use-license-validation';

/**
 * Hook para validar acesso aos sistemas da empresa.
 * 
 * IMPORTANTE: Este hook valida SISTEMAS completos, não features específicas.
 * 
 * Sistemas são produtos completos como:
 * - FleetONE (fleet)
 * - TrackingONE (gps_tracking)
 * 
 * Cada sistema possui seus próprios módulos, que são controlados via:
 * - user_module_assignments (tabela)
 * - useModuleAccess (hook)
 * 
 * @param companyId - ID da empresa
 * @returns Objeto com sistemas e seus status de validação
 */

// Sistemas reais que podem ser validados por licença
const LICENSABLE_SYSTEMS = [
  'gps_tracking',
  'fleet'
] as const;

type LicensableSystem = typeof LICENSABLE_SYSTEMS[number];

interface CompanySystems {
  [key: string]: boolean;
}

export function useCompanySystems(companyId?: string) {
  const { data: licenseResults, isLoading } = useBatchLicenseValidation(
    companyId, 
    LICENSABLE_SYSTEMS as unknown as string[]
  );

  // Converte os resultados de licença para o formato de sistemas
  const systems: CompanySystems = {};
  
  if (licenseResults) {
    LICENSABLE_SYSTEMS.forEach(system => {
      systems[system] = licenseResults[system]?.valid === true;
    });
  }

  return {
    data: systems,
    isLoading,
    error: null
  };
}

// Mantém compatibilidade com código existente
export const useCompanyFeatures = useCompanySystems;

// Hook específico para GPS tracking
export function useGpsFeature(companyId?: string) {
  const { data: licenseResults } = useBatchLicenseValidation(companyId, ['gps_tracking']);
  const license = licenseResults?.gps_tracking;
  
  return {
    hasAccess: license?.valid === true,
    reason: license?.reason,
    isTrial: license?.details?.is_trial,
    trialExpiresAt: license?.details?.trial_expires_at,
    planType: license?.details?.plan_type,
    loading: !licenseResults
  };
}